'use client';

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { getTechnicalSession, getSandboxStatus } from '~/lib/network/technical-session';
import { GamgeeChatSessionRead } from '~/types/session';
import { useGlobalStore } from '~/stores/store-provider';
import { getSession } from '~/lib/session';
import { NotebookLoader } from './notebook-loader';
import { GamgeeNotebook } from '~/components/notebook/gamgee-notebook';

const NotebookSession = ({ sessionId }: { sessionId: string }) => {
  const [session, setSession] = useState<GamgeeChatSessionRead | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { collapsed } = useGlobalStore((state: any) => state);
  const userSession = getSession();

  // Sequential health check: sandbox → notebook
  const { data: sessionStatus } = useQuery({
    queryKey: ['sessionReady', sessionId],
    queryFn: async () => {
      // Step 1: Check sandbox status from backend
      const sandboxStatus = await getSandboxStatus(sessionId);

      // Step 2: If sandbox running and has tunnel, check notebook health
      if (sandboxStatus.status === 'running' && sandboxStatus.tunnel_url) {
        try {
          const healthCheck = await fetch(`${sandboxStatus.tunnel_url}/health`, {
            method: 'GET',
            signal: AbortSignal.timeout(15000),
          });

          if (healthCheck.ok) {
            // Both sandbox and notebook are ready
            return {
              ready: true,
              status: sandboxStatus.status,
              tunnel_url: sandboxStatus.tunnel_url,
              notebook_auth_token: sandboxStatus.notebook_auth_token,
            };
          }
        } catch {
          // Notebook not responding yet, keep polling
        }
      }

      // Not ready yet
      return {
        ready: false,
        status: sandboxStatus.status,
        tunnel_url: sandboxStatus.tunnel_url,
        notebook_auth_token: sandboxStatus.notebook_auth_token,
      };
    },
    refetchInterval: (data) => {
      // Keep polling always to detect if notebook goes down
      // Poll faster when not ready, slower when ready
      return data?.state?.data?.ready ? 5000 : 2000;
    },
    refetchIntervalInBackground: false,
    enabled: !!sessionId && !!session,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const loadSession = async () => {
      try {
        const sessionData = await getTechnicalSession(sessionId);
        setSession(sessionData);
      } catch (error) {
        console.error('Failed to load technical session:', error);
        navigate('/session');
      } finally {
        setIsLoading(false);
      }
    };

    loadSession();
  }, [sessionId, navigate]);

  if (isLoading) {
    return <NotebookLoader />;
  }

  if (!session) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-error">Session not found</div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      {/* Fixed topbar with session title */}
      <div
        className={`fixed top-0 h-16 z-10 ${
          collapsed ? "left-12" : "left-78"
        } right-0 pt-4 bg-base-100 mask-b-from-70%`}
      >
        <h2 className="text-lg text-primary font-medium text-center">
          {session.title}
        </h2>
      </div>

      {/* Main content */}
      <div className="pt-16 h-full">
        {sessionStatus?.ready && sessionStatus.tunnel_url ? (
          <div className="h-full">
            <div className="mx-auto h-full w-full">
              <GamgeeNotebook
                key={`${sessionId}-${sessionStatus.tunnel_url}`}
                serverUrl={sessionStatus.tunnel_url}
                authToken={sessionStatus.notebook_auth_token}
                aiUrl={`${import.meta.env.VITE_BACKEND_URL}/api/v1/notebook/${sessionId}`}
                aiModel="anthropic/claude-sonnet-4-5"
                gamgeeAuthToken={userSession.accessToken || ''}
              />
            </div>
          </div>
        ) : (
          <NotebookLoader status={sessionStatus?.status} />
        )}
      </div>
    </div>
  );
};

export default NotebookSession;
